/**
 * Flow360 API Client — Functions for Flow360 integration.
 *
 * Simplified from flight-sciences branch. Uses sessionStorage instead of
 * cookies for token storage (Supabase Auth handles primary auth session;
 * Flow360 tokens are used for API browsing only).
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

// Portal login URL — must be set via VITE_FLOW360_LOGIN_URL env var.
// Defaults to dev portal for local development.
const FLOW360_LOGIN_URL =
  import.meta.env.VITE_FLOW360_LOGIN_URL ?? "https://my.simulation.cloud/login";

/**
 * Build the return URL for auth redirects.
 *
 * In local dev, we must use a *.dev-simulation.cloud domain so the portal's
 * isFromApp() regex accepts the ref. Requires /etc/hosts entry:
 *   127.0.0.1  thread.dev-simulation.cloud
 *
 * The dev callback host can be overridden via VITE_FLOW360_CALLBACK_HOST.
 */
function getReturnUrl(): string {
  if (import.meta.env.DEV) {
    const port = new URL(window.location.href).port || "5173";
    const callbackHost =
      import.meta.env.VITE_FLOW360_CALLBACK_HOST ?? "thread.dev-simulation.cloud";
    // Must use https:// because the dev portal sends HSTS includeSubDomains,
    // which forces the browser to upgrade all *.dev-simulation.cloud to HTTPS.
    return `https://${callbackHost}:${port}/auth/callback?provider=flow360`;
  }
  // Include the base path (e.g. "/thread") for subpath deployments.
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  return `${window.location.origin}${base}/auth/callback?provider=flow360`;
}

// =============================================================================
// Token Storage (sessionStorage — scoped to tab)
// =============================================================================

export function getFlow360AccessToken(): string | null {
  return sessionStorage.getItem("flow360_access_token");
}

export function getFlow360CognitoSub(): string | null {
  return sessionStorage.getItem("flow360_cognito_sub");
}

export function getFlow360UserEmail(): string | null {
  return sessionStorage.getItem("flow360_user_email");
}

export function clearFlow360Session(): void {
  sessionStorage.removeItem("flow360_access_token");
  sessionStorage.removeItem("flow360_refresh_token");
  sessionStorage.removeItem("flow360_cognito_sub");
  sessionStorage.removeItem("flow360_user_email");
}

// =============================================================================
// Login URL
// =============================================================================

export function getFlow360LoginUrl(): string {
  const returnUrl = getReturnUrl();
  return `${FLOW360_LOGIN_URL}?ref=${encodeURIComponent(returnUrl)}`;
}

/**
 * Redirect to Flow360 login via the SimCloud portal.
 * Local dev uses my.dev-simulation.cloud; prod uses my.simulation.cloud.
 */
export function redirectToFlow360Login(): void {
  window.location.href = getFlow360LoginUrl();
}

// =============================================================================
// Token Exchange
// =============================================================================

export interface TokenExchangeResult {
  auth: {
    accessToken: string;
    refreshToken: string;
    idToken: string;
  };
  user: {
    email: string;
    identityId: string;
  };
  cognito_sub: string | null;
}

/**
 * Exchange a one-time token for full credentials via backend proxy.
 * Stores tokens in sessionStorage.
 */
export async function exchangeOneTimeToken(
  oneTimeToken: string,
): Promise<TokenExchangeResult> {
  const res = await fetch(
    `${API_BASE}/flow360/auth/token/exchange?oneTimeToken=${encodeURIComponent(oneTimeToken)}`,
  );

  if (!res.ok) {
    const text = await res.text().catch(() => "Unknown error");
    throw new Error(`Token exchange failed: ${text}`);
  }

  const data: TokenExchangeResult = await res.json();

  // Store in sessionStorage
  if (data.auth?.accessToken) {
    sessionStorage.setItem("flow360_access_token", data.auth.accessToken);
  }
  if (data.auth?.refreshToken) {
    sessionStorage.setItem("flow360_refresh_token", data.auth.refreshToken);
  }
  if (data.cognito_sub) {
    sessionStorage.setItem("flow360_cognito_sub", data.cognito_sub);
  }
  if (data.user?.email) {
    sessionStorage.setItem("flow360_user_email", data.user.email);
  }

  return data;
}

// =============================================================================
// Fetch Helper (with Flow360 auth headers)
// =============================================================================

function buildFlow360Headers(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getFlow360AccessToken();
  if (token) {
    headers["X-Flow360-Authorization"] = `Bearer ${token}`;
  }
  const userId = getFlow360UserEmail();
  if (userId) {
    headers["X-Flow360-Access-User"] = userId;
  }
  const refreshToken = sessionStorage.getItem("flow360_refresh_token");
  if (refreshToken) {
    headers["X-Flow360-Refresh-Token"] = refreshToken;
  }
  return headers;
}

async function flow360Fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const headers = buildFlow360Headers();

  // Include Supabase JWT so endpoints that write to Thread DB can authenticate.
  // Dynamically import to avoid a hard dependency on supabase from this module.
  try {
    const { supabase } = await import("../lib/supabase");
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) {
      headers["Authorization"] = `Bearer ${data.session.access_token}`;
    }
  } catch {
    // Supabase not available — browsing-only mode
  }

  const res = await fetch(`${API_BASE}/flow360${endpoint}`, {
    ...options,
    headers: { ...headers, ...options?.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Unknown error" }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

// =============================================================================
// Flow360 API Calls
// =============================================================================

export interface AuthStatus {
  authenticated: boolean;
  user: Flow360User | null;
  has_stored_key?: boolean;
}

export interface Flow360User {
  identityId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  personName?: string;
}

export async function checkAuthStatus(): Promise<AuthStatus> {
  return flow360Fetch<AuthStatus>("/auth/status");
}

export interface ProjectListResponse {
  projects: Array<{ id: string; name: string }>;
  total: number;
}

export async function fetchProjects(): Promise<ProjectListResponse> {
  return flow360Fetch<ProjectListResponse>("/projects");
}

// =============================================================================
// Flow360 Browsing API
// =============================================================================

import type {
  Flow360Folder,
  Flow360Geometry,
  Flow360SurfaceMesh,
  Flow360VolumeMesh,
  Flow360Case,
  CaseResults,
  ImportRequest,
  ImportStartResult,
  ImportProgress,
  FullConfigResponse,
  ForkRequest,
  ForkResult,
  SweepRequest,
  SweepResult,
} from "./types";

export interface FolderPage {
  projects: Flow360Folder[];
  total: number;
  page: number;
  size: number;
}

export async function fetchFlow360Folders(opts?: {
  page?: number;
  size?: number;
  search?: string;
}): Promise<FolderPage> {
  const params = new URLSearchParams();
  if (opts?.page != null) params.set("page", String(opts.page));
  if (opts?.size != null) params.set("size", String(opts.size));
  if (opts?.search) params.set("search", opts.search);
  const qs = params.toString() ? `?${params}` : "";
  return flow360Fetch(`/projects${qs}`);
}

export async function fetchProjectThumbnail(
  projectId: string,
): Promise<{ url: string } | null> {
  try {
    return await flow360Fetch<{ url: string }>(
      `/projects/${encodeURIComponent(projectId)}/thumbnail`,
    );
  } catch {
    return null;
  }
}

export async function fetchFlow360Geometries(
  folderId?: string,
): Promise<Flow360Geometry[]> {
  const qs = folderId ? `?folderId=${encodeURIComponent(folderId)}` : "";
  return flow360Fetch(`/geometries${qs}`);
}

export async function fetchFlow360SurfaceMeshes(opts: {
  geometryId?: string;
  folderId?: string;
}): Promise<Flow360SurfaceMesh[]> {
  const params = new URLSearchParams();
  if (opts.geometryId) params.set("geometryId", opts.geometryId);
  if (opts.folderId) params.set("folderId", opts.folderId);
  const qs = params.toString() ? `?${params}` : "";
  return flow360Fetch(`/surface-meshes${qs}`);
}

export async function fetchFlow360VolumeMeshes(opts: {
  surfaceMeshId?: string;
  folderId?: string;
}): Promise<Flow360VolumeMesh[]> {
  const params = new URLSearchParams();
  if (opts.surfaceMeshId) params.set("surfaceMeshId", opts.surfaceMeshId);
  if (opts.folderId) params.set("folderId", opts.folderId);
  const qs = params.toString() ? `?${params}` : "";
  return flow360Fetch(`/volume-meshes${qs}`);
}

export async function fetchFlow360Cases(opts: {
  volumeMeshId?: string;
  folderId?: string;
}): Promise<Flow360Case[]> {
  const params = new URLSearchParams();
  if (opts.volumeMeshId) params.set("volumeMeshId", opts.volumeMeshId);
  if (opts.folderId) params.set("folderId", opts.folderId);
  const qs = params.toString() ? `?${params}` : "";
  return flow360Fetch(`/cases${qs}`);
}

export async function fetchFlow360CaseResults(
  caseId: string,
): Promise<CaseResults> {
  return flow360Fetch(`/cases/${encodeURIComponent(caseId)}/results`);
}

export async function importFlow360Data(
  req: ImportRequest,
): Promise<ImportStartResult> {
  return flow360Fetch("/import", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export async function fetchImportProgress(jobId: string): Promise<ImportProgress> {
  return flow360Fetch(`/import/${encodeURIComponent(jobId)}/progress`);
}

export async function fetchActiveImports(projectId: string): Promise<ImportProgress[]> {
  return flow360Fetch(`/import/active?project_id=${encodeURIComponent(projectId)}`);
}

// =============================================================================
// Fork & Sweep API
// =============================================================================

export async function fetchFullConfig(
  caseId: string,
): Promise<FullConfigResponse> {
  return flow360Fetch(`/cases/${encodeURIComponent(caseId)}/full-config`);
}

export async function forkCase(req: ForkRequest): Promise<ForkResult> {
  return flow360Fetch("/cases/fork", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export async function submitSweep(req: SweepRequest): Promise<SweepResult> {
  return flow360Fetch("/cases/sweep", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

// =============================================================================
// Helpers
// =============================================================================

export function getStatusColor(
  status: string,
): "success" | "warning" | "error" | "default" {
  switch (status) {
    case "processed":
    case "completed":
      return "success";
    case "generating":
    case "running":
    case "pending":
    case "queued":
    case "submitted":
    case "uploading":
    case "uploaded":
      return "warning";
    case "error":
    case "diverged":
      return "error";
    default:
      return "default";
  }
}


// =============================================================================
// Wing Area Detection
// =============================================================================

export interface WingAreaVizData {
  wing_vertices: number[][];
  wing_faces: number[][];
  projection_vertices: number[][];
  ghost_vertices: number[][];
  ghost_faces: number[][];
  bbox: [number[], number[]];
  lift_axis_idx: number;
  planform_axes: [number, number];
  planform_outline: number[][];
  trapezoid_outline: number[][];
  rasterized_outline?: number[][];
  gross_rasterized_outline?: number[][];
  span_extent: number;
  chord_extent: number;
  standard_mean_chord: number;
  root_chord: number;
  tip_chord: number;
  s_net: number;
  s_gross: number;
  span_line: number[][];
  chord_line: number[][];
  smc_line: number[][];
}

export interface WingAreaDetectionResult {
  projected_area: number;
  s_net: number;
  s_gross: number;
  wing_span: number;
  root_chord: number;
  tip_chord: number;
  standard_mean_chord: number;
  face_indices: number[];
  n_faces_total: number;
  n_faces_selected: number;
  confidence: string;
  method: string;
  detection_id: string | null;
  length_unit?: string;
  viz?: WingAreaVizData;
  cached?: boolean;
}

export interface WingAreaConfirmResult {
  detection_id: string;
  original_area: number;
  confirmed_area: number;
  status: string;
}

export async function detectWingArea(
  surfaceMeshId: string,
  projectId: string,
  options: {
    liftAxis?: string;
    normalThreshold?: number;
    resolution?: number;
    includeViz?: boolean;
    force?: boolean;
  } = {},
): Promise<WingAreaDetectionResult> {
  const payload: Record<string, unknown> = {
    surface_mesh_id: surfaceMeshId,
    project_id: projectId,
    lift_axis: options.liftAxis ?? "z",
    include_viz: options.includeViz ?? true,
    force: options.force ?? true,
  };
  if (options.normalThreshold != null) payload.normal_threshold = options.normalThreshold;
  if (options.resolution != null) payload.resolution = options.resolution;

  return flow360Fetch<WingAreaDetectionResult>("/detect-wing-area", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// =============================================================================
// Visualization — pre-signed S3 URLs for UVF viewer
// =============================================================================

export interface VisualizationUrls {
  files: Record<string, string>;
}

export async function fetchVisualizationUrls(
  entityType: string,
  entityId: string,
  projectId?: string,
): Promise<VisualizationUrls> {
  const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
  return flow360Fetch<VisualizationUrls>(
    `/visualize/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}${qs}`,
  );
}

export async function confirmWingArea(
  detectionId: string,
  confirmedArea: number,
  projectId: string,
): Promise<WingAreaConfirmResult> {
  return flow360Fetch<WingAreaConfirmResult>("/confirm-wing-area", {
    method: "POST",
    body: JSON.stringify({
      detection_id: detectionId,
      confirmed_area: confirmedArea,
      project_id: projectId,
    }),
  });
}

// =============================================================================
// Refresh / Backfill
// =============================================================================

export interface RefreshResult {
  total_runs: number;
  status_updated: number;
  results_imported: number;
  config_backfilled: number;
  errors: string[];
}

export async function refreshProjectRuns(
  projectId: string,
  branch = "main",
): Promise<RefreshResult> {
  return flow360Fetch<RefreshResult>(
    `/refresh-project?project_id=${encodeURIComponent(projectId)}&branch=${encodeURIComponent(branch)}`,
    { method: "POST" },
  );
}
